// The two documents a plan closes with — Guidelines and Policy — as Naurist
// writes them by default, which is the text every practice starts on in
// My brand → Guidelines & policy.
//
// A specimen, like `invoiceSample` beside it: the preview shows the closing
// sheets a client gets, and this is what is printed on them until a practice
// writes its own. The generated PDF prints the stored documents instead — the
// server's rendering of them, clause tokens already resolved — so nothing
// here is ever sent to anybody.

/** One numbered or bulleted line: the phrase it opens with, and the rest. */
export interface DocumentItem {
  /** Set in bold, as the PDF sets it, and followed by a colon. */
  term: string;
  text: string;
}

export interface DocumentSection {
  heading: string;
  /** Numbered, as Guidelines is, against Policy's bullets. */
  ordered: boolean;
  items: DocumentItem[];
}

export interface PlanDocumentSpecimen {
  /** The eyebrow over the first sheet — "Guidelines", "Policy". */
  title: string;
  sections: DocumentSection[];
}

export const guidelinesSpecimen: PlanDocumentSpecimen = {
  title: "Guidelines",
  sections: [
    {
      heading: "General Guidelines",
      ordered: true,
      items: [
        {
          term: "Follow the meal plan as advised",
          text: "Follow the recommended foods, timings, portions, quantities, servings, and preparation instructions as closely as possible.",
        },
        {
          term: "Meal timings are recommendations",
          text: "Maintain reasonably consistent meal and snack timings. Minor adjustments may be made to suit your routine unless specific timings have been advised.",
        },
        {
          term: "Do not skip meals unnecessarily",
          text: "Avoid regularly skipping meals, reducing portions significantly, or making major changes without consulting us.",
        },
        {
          term: "Stay adequately hydrated",
          text: "Drink water regularly and maintain fluid intake according to your individual needs and professional advice.",
        },
        {
          term: "Use suitable food substitutions",
          text: "Use only substitutions recommended or approved, especially where nutritional or medical considerations apply.",
        },
        {
          term: "Inform your nutritionist about changes in your health",
          text: "Report any new medical condition, medication, supplement, allergy, intolerance, pregnancy, surgery, hospitalisation, or other relevant health change.",
        },
        {
          term: "Follow medical advice separately",
          text: "This plan does not replace medical advice, diagnosis, treatment, or prescribed medication. Continue medical care as advised by your healthcare professional.",
        },
        {
          term: "Food quality and preparation matter",
          text: "Choose fresh, hygienically prepared food and follow appropriate storage, cooking, and food-safety practices. Avoid spoiled or improperly stored food.",
        },
        {
          term: "Listen to your body",
          text: "If you experience unusual, severe, or persistent symptoms, stop the relevant activity where appropriate and seek professional medical advice.",
        },
        {
          term: "Individual response may vary",
          text: "Nutritional needs and results vary by individual, including factors such as health, lifestyle, adherence, and other personal circumstances.",
        },
        {
          term: "Do not share your plan without professional review",
          text: "This plan is prepared for the intended client and should not be used by another person without professional assessment.",
        },
      ],
    },
    {
      heading: "Client Responsibilities",
      ordered: true,
      items: [
        {
          term: "Provide accurate information",
          text: "Provide complete and accurate information about your health, medications, supplements, allergies, dietary preferences, lifestyle, and other relevant factors.",
        },
        {
          term: "Report relevant changes promptly",
          text: "Inform us of any significant change that may affect your nutritional requirements or plan.",
        },
        {
          term: "Adherence affects outcomes",
          text: "Results depend on adherence and other factors such as activity, sleep, stress, health, medications, genetics, and lifestyle. No specific result or timeline is guaranteed.",
        },
        {
          term: "Ask before making major changes",
          text: "Consult before making significant changes to meals, portions, substitutions, supplements, restrictions, or other recommendations.",
        },
      ],
    },
    {
      heading: "Communication",
      ordered: true,
      items: [
        {
          term: "Plan validity and revisions",
          text: "Your meal plan may be reviewed or modified as your needs, goals, health, progress, preferences, or lifestyle change.",
        },
        {
          term: "Response times",
          text: "Nutritionists may not be available for immediate responses. Routine queries should be addressed through the agreed consultation or communication process. This service is not an emergency medical service.",
        },
        {
          term: "Emergency situations",
          text: "In a medical emergency, severe allergic reaction, or serious or worsening symptoms, seek immediate medical attention. Do not wait for a response from your nutritionist.",
        },
      ],
    },
  ],
};

export const policySpecimen: PlanDocumentSpecimen = {
  title: "Policy",
  sections: [
    {
      heading: "Terms & Conditions",
      ordered: false,
      items: [
        {
          term: "Purpose and scope",
          text: "This meal plan provides personalised nutritional guidance based on the information provided and the nutritionist’s professional assessment. It does not replace medical diagnosis, treatment, or emergency care.",
        },
        {
          term: "No guaranteed results",
          text: "Specific outcomes, including weight, body composition, blood markers, performance, or other results, cannot be guaranteed.",
        },
        {
          term: "Professional judgement",
          text: "Recommendations may be updated as new information becomes available or as your circumstances change.",
        },
        {
          term: "Third-party products and services",
          text: "Any food, supplement, product, service, restaurant, brand, or other third-party recommendation does not guarantee its quality, safety, effectiveness, availability, or suitability.",
        },
        {
          term: "Client responsibility",
          text: "The client is responsible for following the plan, providing accurate health information, checking food ingredients where necessary, and seeking appropriate medical advice when required.",
        },
        {
          term: "Allergies and intolerances",
          text: "Inform your nutritionist of all known or suspected allergies, intolerances, and dietary restrictions. The nutritionist cannot control the ingredients, preparation, or cross-contamination of food prepared by third parties.",
        },
        {
          term: "Limitation of responsibility",
          text: "To the extent permitted by applicable law, the nutritionist is not responsible for outcomes, adverse reactions, losses, or consequences resulting from inaccurate or incomplete information, failure to follow recommendations, unauthorised changes, undisclosed medical conditions, medication interactions, food contamination, or circumstances beyond reasonable control.",
        },
        {
          term: "Confidentiality",
          text: "Information shared during the nutrition engagement should be kept confidential, subject to applicable law and situations where disclosure is required or necessary for safety.",
        },
        {
          term: "Plan ownership and use",
          text: "This meal plan is for the intended client’s personal use and should not be copied, redistributed, resold, published, or used as professional advice for others without permission.",
        },
        {
          term: "Changes to recommendations",
          text: "The nutritionist may modify or discontinue recommendations based on your progress, health status, updated information, professional judgement, or other relevant circumstances.",
        },
        {
          term: "Acknowledgement",
          text: "By following this meal plan, the client acknowledges receipt of nutritional guidance and responsibility for their food, lifestyle, and healthcare decisions, subject to applicable law.",
        },
        {
          term: "Acceptance of terms",
          text: "Continued participation in the nutrition engagement may constitute acknowledgement of these guidelines and terms, along with any separate consultation, payment, cancellation, or service terms communicated by the nutritionist.",
        },
      ],
    },
  ],
};
